import { useCallback, useEffect, useRef, useState } from 'react';
import type { IScreensLogging } from '../../../../../api/types';
import { useLogAnalyticsEvent } from '../../../../../hooks/useLogAnalyticsEvent';
import { useRetainedEffect } from '../../../../../hooks/retainedState';

export const useLogDisplayCarousel = (
  logging?: IScreensLogging,
  isVisible?: boolean,
  isAwaitingDataChange?: boolean,
) => {
  const logged = useRef(false);
  // 외곽 필터로 carousel 데이터가 보존되는 경우 logging ref가 그대로 유지되어
  // [logging] effect가 안 도는 케이스를 커버. resetDisplayCarousel 호출 시
  // counter bump로 강제 재실행.
  const [resetCounter, setResetCounter] = useState(0);

  // bridge 직접 호출 대신 AnalyticsContext 자동 머지 wrapper 사용 — SCREEN 등 공용 필드 누락 방지.
  const logAnalyticsEvent = useLogAnalyticsEvent();
  // 명령형 logDisplayCarousel이 stale closure 없이 latest 값을 읽도록 ref 동기화.
  const loggingRef = useRef(logging);
  loggingRef.current = logging;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;
  // 부모(AtomicRenderer) viewport 가시성. inner FlatList 의 onViewableItemsChanged 가
  // 부모 viewport 와 무관하게 mount 즉시 viewable 을 보고하므로, 명령형 호출에서도
  // 부모 가시성을 한 번 더 가드해 viewport 밖 발화를 차단한다.
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const logAnalyticsEventRef = useRef(logAnalyticsEvent);
  logAnalyticsEventRef.current = logAnalyticsEvent;

  const resetDisplayCarousel = useCallback(() => {
    logged.current = false;
    setResetCounter(c => c + 1);
  }, []);

  // DISPLAY_CAROUSEL 은 useLayoutEffect 의 자연 발화 경로 하나만 사용.
  // (이전엔 명령형 logDisplayCarousel 을 노출해 inner FlatList 콜백에서 GOODS 보다
  // 먼저 호출했으나, Android 와 정책 정합 + 다른 컴포넌트 정책 확산 부담 이슈로 제거.)
  const fireDisplayCarousel = useCallback(() => {
    if (logged.current) return;
    if (isVisibleRef.current === false) return;
    const currentLogging = loggingRef.current;
    if (!currentLogging?.analytics) return;
    if (isAwaitingDataChangeRef.current) return;
    logged.current = true;
    logAnalyticsEventRef.current({
      name: 'DISPLAY_CAROUSEL',
      analytics: currentLogging.analytics,
      ably_only_log: currentLogging.ably_only_log,
    });
  }, []);

  useEffect(() => {
    logged.current = false;
  }, [logging]);

  useRetainedEffect(
    `atomic-carousel-${JSON.stringify(logging ?? {})}`,
    () => {
      if (!isVisible) return;
      fireDisplayCarousel();
    },
    [
      isVisible,
      logging,
      resetCounter,
      isAwaitingDataChange,
      fireDisplayCarousel,
    ],
  );

  return { resetDisplayCarousel };
};
